import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, Checkbox, IconButton, Switch, TextField, Tooltip, Typography } from '@mui/material';
import FolderSpecialOutlinedIcon from '@mui/icons-material/FolderSpecialOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { toast } from 'react-toastify';
import EnumsService from '../../../Services/enums.service.js';
import AutocompleteSelect from '../../Widgets/AutocompleteSelect/AutocompleteSelect.jsx';
import CustomDeleteConfirmationModal from '../../CustomDeleteConfirmationModal/CustomDeleteConfirmationModal.js';
import addIconImage from '../../../assests/images/Icons/addIcon.svg';
import { PERMANENT_FILE_TITLE_NAME, isPermanentFileName } from '../../../Utils/otherDocumentSections';
import cardStyles from './OtherDocumentsTitlesCard.module.scss';

const inputSx = {
    height: '40px',
    padding: '0 10px',
    borderRadius: '8px',
    background: '#F5F5F6',
    border: '1px solid transparent',
    '&:focus-within': {
        border: '2px solid var(--Foundation-Blue-Normal, #304FFF)',
    },
};

const OtherDocumentsTitlesCard = ({ t, direction, styles, user, token, types = [], onTypesChanged, onTitlesChanged }) => {
    const [titles, setTitles] = useState([]);
    const [busy, setBusy] = useState(false);
    const [isAdding, setIsAdding] = useState(false);
    const [newName, setNewName] = useState('');
    const [newHidden, setNewHidden] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [editingName, setEditingName] = useState('');
    const [selectedIds, setSelectedIds] = useState([]);
    const [deleteId, setDeleteId] = useState(null);
    const [permanentHidden, setPermanentHidden] = useState(true);

    const accountantId = user?.accountantId;
    const hasAuth = !!accountantId && typeof token === 'string' && token.length > 0;

    const fetchTitles = useCallback(async () => {
        if (!hasAuth) return;
        try {
            const response = await EnumsService.GetOtherDocumentsTitlesByAccountantId(accountantId, token);
            if (response.ok) {
                const data = await response.json();
                setTitles(Array.isArray(data) ? data : []);
            }
        } catch (error) {
            console.error('Error fetching other documents titles:', error);
        }
    }, [accountantId, token, hasAuth]);

    useEffect(() => {
        fetchTitles();
    }, [fetchTitles]);

    const titleNameById = useMemo(
        () => new Map(titles.map((title) => [title.id, title.name])),
        [titles]
    );

    const typeOptions = useMemo(
        () => (Array.isArray(types) ? types : [])
            .filter((type) => type?.id)
            .map((type) => ({
                value: type.id,
                titleId: type.titleId || null,
                label: type.name,
            })),
        [types]
    );

    const runRequest = async (request, successKey, errorKey) => {
        setBusy(true);
        try {
            const response = await request();
            if (response.ok) {
                if (successKey) toast.success(t(successKey));
                return true;
            }
            const errorText = await response.text();
            toast.error(`${t(errorKey)}: ${errorText}`);
        } catch (error) {
            console.error(error);
            toast.error(t(errorKey));
        } finally {
            setBusy(false);
        }
        return false;
    };

    const handleAdd = async () => {
        const name = newName.trim();
        if (!name) return;
        const ok = await runRequest(
            () => EnumsService.AddOtherDocumentsTitle(accountantId, name, token, newHidden),
            'TitleAddedSuccessfully',
            'TitleAddError'
        );
        if (ok) {
            setIsAdding(false);
            setNewName('');
            setNewHidden(false);
            await fetchTitles();
            onTitlesChanged?.();
        }
    };

    const handleRename = async (title) => {
        const name = editingName.trim();
        if (!name || name === title.name) {
            setEditingId(null);
            return;
        }
        const ok = await runRequest(
            () => EnumsService.UpdateOtherDocumentsTitle(accountantId, title.id, { name }, token),
            'TitleUpdatedSuccessfully',
            'TitleUpdateError'
        );
        if (ok) {
            setEditingId(null);
            setTitles((prev) => prev.map((item) => (item.id === title.id ? { ...item, name } : item)));
            onTitlesChanged?.();
        }
    };

    const setHidden = async (titleIds, hidden) => {
        if (titleIds.length === 0) return;
        const ok = await runRequest(
            () => EnumsService.SetOtherDocumentsTitlesHidden(accountantId, titleIds, hidden, token),
            hidden ? 'TitlesHiddenSuccessfully' : 'TitlesShownSuccessfully',
            'TitleUpdateError'
        );
        if (ok) {
            setTitles((prev) => prev.map((item) => (titleIds.includes(item.id) ? { ...item, hiddenFromClient: hidden } : item)));
            onTitlesChanged?.();
        }
    };

    const handleDelete = async () => {
        const id = deleteId;
        setDeleteId(null);
        if (!id) return;
        const ok = await runRequest(
            () => EnumsService.DeleteOtherDocumentsTitle(id, token),
            'TitleDeletedSuccessfully',
            'TitleDeleteError'
        );
        if (ok) {
            setSelectedIds((prev) => prev.filter((selected) => selected !== id));
            await fetchTitles();
            onTypesChanged?.();
            onTitlesChanged?.();
        }
    };

    const handleAssignedTypesChange = async (title, newValue) => {
        const nextIds = new Set((newValue || []).map((option) => option.value));
        const currentIds = new Set(typeOptions.filter((option) => option.titleId === title.id).map((option) => option.value));
        const added = [...nextIds].filter((id) => !currentIds.has(id));
        const removed = [...currentIds].filter((id) => !nextIds.has(id));
        if (added.length === 0 && removed.length === 0) return;

        let ok = true;
        if (added.length > 0) {
            ok = await runRequest(
                () => EnumsService.AssignTypesToOtherDocumentsTitle(accountantId, title.id, added, token),
                null,
                'TypesAssignError'
            );
        }
        if (ok && removed.length > 0) {
            ok = await runRequest(
                () => EnumsService.AssignTypesToOtherDocumentsTitle(accountantId, null, removed, token),
                null,
                'TypesAssignError'
            );
        }
        if (ok) toast.success(t('TypesAssignedSuccessfully'));
        onTypesChanged?.();
    };

    const hasPermanentFileTitle = titles.some((title) => title.name === PERMANENT_FILE_TITLE_NAME);
    const builtInPermanentTypes = typeOptions.filter((option) => !option.titleId && isPermanentFileName(option.label));

    const handleConvertPermanentFile = async () => {
        const ok = await runRequest(
            () => EnumsService.EnsurePermanentFileTitle(accountantId, permanentHidden, token),
            'PermanentFileConvertedSuccessfully',
            'TitleAddError'
        );
        if (ok) {
            await fetchTitles();
            onTypesChanged?.();
            onTitlesChanged?.();
        }
    };

    const toggleSelected = (id) => {
        setSelectedIds((prev) => (prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]));
    };

    const allSelected = titles.length > 0 && selectedIds.length === titles.length;

    return (
        <div className={styles.propertyCard} dir={direction}>
            <div className={styles.propertyHeader}>
                <div className={styles.propertyTitleWrap}>
                    <span className={styles.cardIcon}><FolderSpecialOutlinedIcon /></span>
                    <h3 className={styles.propertyTitle}>{t('OtherDocumentsTitles')}</h3>
                </div>
                <div className={styles.propertyActions}>
                    <IconButton className={styles.propertyActionBtn} onClick={() => setIsAdding(true)} disabled={busy}>
                        <img src={addIconImage} alt="Add" />
                    </IconButton>
                </div>
            </div>
            <p className={styles.propertyHint}>{t('OtherDocumentsTitlesHint')}</p>

            {isAdding && (
                <Box className={cardStyles.addRow}>
                    <TextField
                        autoFocus
                        size="small"
                        variant="standard"
                        placeholder={t('TitleName')}
                        value={newName}
                        onChange={(e) => setNewName(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') handleAdd(); }}
                        InputProps={{ disableUnderline: true, sx: inputSx }}
                        inputProps={{ style: { textAlign: direction === 'ltr' ? 'left' : 'right' } }}
                        className={cardStyles.grow}
                    />
                    <label className={cardStyles.inlineCheck}>
                        <Checkbox size="small" checked={newHidden} onChange={(e) => setNewHidden(e.target.checked)} />
                        <span>{t('HideTitleFromClient')}</span>
                    </label>
                    <IconButton size="small" onClick={handleAdd} disabled={!newName.trim() || busy}>
                        <CheckIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                        size="small"
                        onClick={() => { setIsAdding(false); setNewName(''); setNewHidden(false); }}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>
            )}

            {hasAuth && !hasPermanentFileTitle && (
                <Box className={`${cardStyles.item} ${cardStyles.builtInItem}`}>
                    <Box className={cardStyles.itemHeader}>
                        <span className={`${cardStyles.name} ${cardStyles.grow}`}>
                            {PERMANENT_FILE_TITLE_NAME}
                            <span className={cardStyles.builtInTag}>{t('BuiltInSection')}</span>
                        </span>
                        <label className={cardStyles.inlineCheck}>
                            <Checkbox size="small" checked={permanentHidden} onChange={(e) => setPermanentHidden(e.target.checked)} />
                            <span>{t('HideTitleFromClient')}</span>
                        </label>
                        <Button
                            size="small"
                            variant="contained"
                            disabled={busy}
                            onClick={handleConvertPermanentFile}
                            className={cardStyles.convertBtn}
                        >
                            {t('AddPermanentFileAsCategory')}
                        </Button>
                    </Box>
                    <Typography variant="body2" className={cardStyles.empty}>
                        {builtInPermanentTypes.length > 0
                            ? builtInPermanentTypes.map((option) => option.label).join(', ')
                            : t('NoDocumentTypes')}
                    </Typography>
                </Box>
            )}

            {titles.length > 0 && (
                <Box className={cardStyles.bulkBar}>
                    <label className={cardStyles.inlineCheck}>
                        <Checkbox
                            size="small"
                            checked={allSelected}
                            indeterminate={selectedIds.length > 0 && !allSelected}
                            onChange={() => setSelectedIds(allSelected ? [] : titles.map((title) => title.id))}
                        />
                        <span>{t('Select all')}</span>
                    </label>
                    <Box className={cardStyles.bulkActions}>
                        <Button
                            size="small"
                            startIcon={<VisibilityOffIcon />}
                            disabled={selectedIds.length === 0 || busy}
                            onClick={() => setHidden(selectedIds, true)}
                            className={cardStyles.bulkBtn}
                        >
                            {t('HideSelected')}
                        </Button>
                        <Button
                            size="small"
                            startIcon={<VisibilityIcon />}
                            disabled={selectedIds.length === 0 || busy}
                            onClick={() => setHidden(selectedIds, false)}
                            className={cardStyles.bulkBtn}
                        >
                            {t('ShowSelected')}
                        </Button>
                    </Box>
                </Box>
            )}

            {titles.length === 0 && !isAdding && (
                <Typography variant="body2" className={cardStyles.empty}>{t('NoTitlesYet')}</Typography>
            )}

            <Box className={cardStyles.list}>
                {titles.map((title) => {
                    const assigned = typeOptions.filter((option) => option.titleId === title.id);
                    const options = typeOptions.map((option) => ({
                        ...option,
                        label: option.titleId && option.titleId !== title.id && titleNameById.has(option.titleId)
                            ? `${option.label} (${titleNameById.get(option.titleId)})`
                            : option.label,
                    }));
                    const isEditing = editingId === title.id;

                    return (
                        <Box key={title.id} className={cardStyles.item}>
                            <Box className={cardStyles.itemHeader}>
                                <Checkbox
                                    size="small"
                                    checked={selectedIds.includes(title.id)}
                                    onChange={() => toggleSelected(title.id)}
                                />
                                {isEditing ? (
                                    <TextField
                                        autoFocus
                                        size="small"
                                        variant="standard"
                                        value={editingName}
                                        onChange={(e) => setEditingName(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') handleRename(title);
                                            if (e.key === 'Escape') setEditingId(null);
                                        }}
                                        InputProps={{ disableUnderline: true, sx: inputSx }}
                                        inputProps={{ style: { textAlign: direction === 'ltr' ? 'left' : 'right' } }}
                                        className={cardStyles.grow}
                                    />
                                ) : (
                                    <span className={`${cardStyles.name} ${cardStyles.grow}`} title={title.name}>{title.name}</span>
                                )}
                                {isEditing ? (
                                    <>
                                        <IconButton size="small" onClick={() => handleRename(title)} disabled={busy}>
                                            <CheckIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton size="small" onClick={() => setEditingId(null)}>
                                            <CloseIcon fontSize="small" />
                                        </IconButton>
                                    </>
                                ) : (
                                    <>
                                        <Tooltip title={t('HideTitleFromClient')}>
                                            <label className={cardStyles.inlineCheck}>
                                                <Switch
                                                    size="small"
                                                    checked={!!title.hiddenFromClient}
                                                    onChange={(e) => setHidden([title.id], e.target.checked)}
                                                    disabled={busy}
                                                />
                                                <span>{t('HiddenFromClient')}</span>
                                            </label>
                                        </Tooltip>
                                        <IconButton
                                            size="small"
                                            onClick={() => { setEditingId(title.id); setEditingName(title.name); }}
                                        >
                                            <EditOutlinedIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton size="small" onClick={() => setDeleteId(title.id)} disabled={busy}>
                                            <DeleteOutlineIcon fontSize="small" />
                                        </IconButton>
                                    </>
                                )}
                            </Box>
                            <AutocompleteSelect
                                options={options}
                                selectedValues={options.filter((option) => assigned.some((a) => a.value === option.value))}
                                onChange={(event, newValue) => handleAssignedTypesChange(title, newValue)}
                                label={t('AssignedTypes')}
                                placeholder={t('SelectDocumentTypes')}
                                isMultiple={true}
                                direction={direction}
                                width="100%"
                                isDisabled={busy}
                            />
                        </Box>
                    );
                })}
            </Box>

            <CustomDeleteConfirmationModal
                isVisible={!!deleteId}
                onClose={() => setDeleteId(null)}
                onDelete={handleDelete}
            />
        </div>
    );
};

export default OtherDocumentsTitlesCard;
